<template>
  <v-container>
    <p class="step-title">
      {{ $t('contactStep.title') }}
    </p>
    <HfrCard v-if="userInfo && Object.keys(userInfo).length > 0">
      <div class="field-warning mb-0">{{ $t('global.prefilledDataWarning') }} </div>
    </HfrCard>
    <HfrCard>
      <v-form ref="administrativeContactForm">
        <v-checkbox
          v-model="noAdministrativeContact"
          :label="$t('contactStep.noAdministrativeContact')">
        </v-checkbox>
        <div v-show="!noAdministrativeContact">
          <AdministrativeContactForm
            v-for="(contact, index) in administrativeContacts"
            :contact="contact"
            :index="index"
            :key="contact.uid"
            @removeAdministrativeContact="
              removeAdministrativeContact(contact.uid)
            "
            @input="updateAdministrativeContact(contact.uid, $event)" />
          <v-btn
            @click="addAdministrativeContact"
            color="black"
            class="btn-multiline white--text pa-8"
            width="100%">
            <v-icon class="mr-1">mdi-plus</v-icon>
            <span class="text-wrap">
              {{ $t('contactStep.addContactBtn') }}
            </span>
          </v-btn>
        </div>
      </v-form>
    </HfrCard>
    <v-divider></v-divider>

    <p class="step-title mt-4">{{ $t('contactStep.emergencyContacts') }}</p>
    <HfrCard class="pa-0">
      <v-form ref="emergencyContactForm">
        <v-checkbox
          v-model="noEmergencyContact"
          :label="$t('contactStep.noEmergencyContact')">
        </v-checkbox>
        <div v-show="!noEmergencyContact">
          <EmergencyContactForm
            v-for="(contact, index) in emergencyContacts"
            :contact="contact"
            :index="index"
            :key="contact.uid"
            @removeEmergencyContact="removeEmergencyContact(contact.uid)"
            @input="updateEmergencyContact(contact.uid, $event)" />
          <v-btn
            @click="addEmergencyContact"
            color="black"
            class="btn-multiline white--text pa-8"
            width="100%">
            <v-icon class="mr-1">mdi-plus</v-icon>
            <span class="text-wrap">
              {{ $t('contactStep.addContactBtn') }}
            </span>
          </v-btn>
        </div>
      </v-form>
    </HfrCard>
  </v-container>
</template>
<script>
import HfrCard from '../ui/HfrCard.vue';
import AdministrativeContactForm from '../ui/AdministrativeContactForm.vue';
import EmergencyContactForm from '../ui/EmergencyContactForm.vue';
import { parseDate } from '@/helpers/dates';
import { mapActions, mapState } from 'vuex';
import uniqueId from 'lodash.uniqueid';
import { requiredRule } from '@/helpers/validationRules';

export default {
  name: 'ContactStep',
  data() {
    return {
      administrativeContacts: [
        {
          uid: uniqueId(),
          relationship: null,
          lastName: null,
          firstName: null,
          street: null,
          zip: null,
          city: null,
          email: null
        }
      ],
      emergencyContacts: [
        { uid: uniqueId(), relationship: null, lastName: null, firstName: null, phone: null }
      ],
      noAdministrativeContact:
        process.env.VUE_APP_PREFILL_FORMS === 'true' ? true : null,
      noEmergencyContact:
        process.env.VUE_APP_PREFILL_FORMS === 'true' ? true : null,
      rules: {
        required: requiredRule
      },
      stepNumber: 6
    };
  },
  components: { AdministrativeContactForm, EmergencyContactForm, HfrCard },
  watch: {
    userInfo: {
      handler() {
        this.fillFormDataWithUserInfo();
      }
    },
    navigateToNextStep: {
      handler(navigateToNextStep) {
        if (this.currentStep === this.stepNumber && navigateToNextStep) {
          this.validateContactsForm();
        }
      }
    }
  },
  methods: {
    ...mapActions('navigation', ['doNextStep', 'toggleNavigateToNextStep']),
    ...mapActions('preAdmission', ['storeContactsStep']),
    doParseDate(date) {
      parseDate(date);
    },
    fillFormDataWithUserInfo() {
      this.noAdministrativeContact = null
      this.noEmergencyContact = null
      this.administrativeContacts = [];
      this.emergencyContacts = [];

      if (this.userInfo) {
        this.userInfo?.administrative_contacts?.forEach((contact) => {
          this.administrativeContacts.push({
            uid: uniqueId(),
            relationship: contact.relationship,
            lastName: contact.last_name,
            firstName: contact.first_name,
            street: contact.addresses[0]?.street,
            zip: contact.addresses[0]?.zip_code,
            city: contact.addresses[0]?.city,
            email: contact.emails[0]?.address,
            phone: contact.phones[0]?.number
          });
        });
        this.userInfo?.emergency_contacts?.forEach((contact) => {
          this.emergencyContacts.push({
            uid: uniqueId(),
            relationship: contact.relationship,
            lastName: contact.last_name,
            firstName: contact.first_name,
            street: contact.addresses[0]?.street,
            zip: contact.addresses[0]?.zip_code,
            city: contact.addresses[0]?.city,
            email: contact.emails[0]?.address,
            phone: contact.phones[0]?.number
          });
        });
      } else {
        this.administrativeContacts.push({
          uid: uniqueId(),
          relationship: null,
          lastName: null,
          firstName: null,
          street: null,
          zip: null,
          city: null,
          email: null
        });
        this.$refs.administrativeContactForm.reset();
      }
      this.$refs.administrativeContactForm.resetValidation();
    },
    addAdministrativeContact() {
      this.administrativeContacts.push({
        uid: uniqueId(),
        relationship: null,
        lastName: null,
        firstName: null,
        street: null,
        zip: null,
        city: null,
        email: null
      });
    },
    removeAdministrativeContact(uid) {
      this.administrativeContacts = this.administrativeContacts.filter(
        (c) => c.uid !== uid
      );
    },
    addEmergencyContact() {
      this.emergencyContacts.push({
        uid: uniqueId(),
        relationship: null,
        lastName: null,
        firstName: null,
        phone: null
      });
    },
    removeEmergencyContact(uid) {
      this.emergencyContacts = this.emergencyContacts.filter(
        (c) => c.uid !== uid
      );
    },
    validateContactsForm() {
      const adminContactsFormValid =
        this.noAdministrativeContact === true ||
        this.$refs.administrativeContactForm.validate();
      const emergencyContactsFormValid =
        this.noEmergencyContact === true ||
        this.$refs.emergencyContactForm.validate();
      if (adminContactsFormValid && emergencyContactsFormValid) {
        this.storeContactsStep({
          administrativeContacts:
            this.noAdministrativeContact === true
              ? []
              : [...this.administrativeContacts],
          emergencyContacts:
            this.noEmergencyContact === true ? [] : [...this.emergencyContacts]
        });
        this.doNextStep();
      }

      this.toggleNavigateToNextStep();
    },
    updateAdministrativeContact(uid, value) {
      const index = this.administrativeContacts.indexOf(
        this.administrativeContacts.find((c) => c.uid === uid)
      );
      this.administrativeContacts[index] = value;
      this.$set(this.administrativeContacts, index, value);
    },
    updateEmergencyContact(uid, value) {
      const index = this.emergencyContacts.indexOf(
        this.emergencyContacts.find((c) => c.uid === uid)
      );
      this.emergencyContacts[index] = value;
      this.$set(this.emergencyContacts, index, value);
    }
  },
  computed: {
    ...mapState('preAdmission', ['userInfo']),
    ...mapState('navigation', ['navigateToNextStep', 'currentStep'])
  }
};
</script>
<style lang="scss">
.btn-multiline > span {
  width: 100%;
}

.white--text {
  color: white;
}
</style>
